import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";

const RatePlanForm = ({ handleClose, handleSubmit: onSubmit, initialData }) => {
  const [hotels, setHotels] = React.useState([]);

  React.useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await fetch("https://api.esthell.com/Hotel");
        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            setHotels(data.data);
          }
        }
      } catch (error) {
        console.error("Error fetching hotels:", error);
      }
    };
    fetchHotels();
  }, []);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialData || {
      hotel_id: "",
      name: "",
      description: "",
      rate_type: "",
      base_plan_id: "",
      markup_type: "",
      markup_value: "",
      inclusion_codes: [],
      cancellation_policy_id: "",
      min_length_of_stay: 1,
      max_length_of_stay: "",
      is_active: true,
    },
  });

  const rateTypes = ["BAR", "PACKAGE", "PROMOTIONAL", "CORPORATE", "WHOLESALE"];
  const markupTypes = ["PERCENTAGE", "FIXED"];

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {initialData ? "Edit Rate Plan" : "New Rate Plan"}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Controller
            name="hotel_id"
            control={control}
            rules={{ required: "Hotel is required" }}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel>Hotel Name</InputLabel>
                <Select {...field} label="Hotel Name" error={!!errors.hotel_id}>
                  <MenuItem value="">
                    <em>Select Hotel</em>
                  </MenuItem>
                  {hotels
                    .filter((hotel) => hotel.is_active === true)
                    .map((hotel) => (
                      <MenuItem key={hotel.id} value={hotel.hotel_id}>
                        {hotel.name} ({hotel.property_code})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="name"
            control={control}
            rules={{ required: "Name is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Description"
                multiline
                rows={3}
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="rate_type"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel>Rate Type</InputLabel>
                <Select {...field} label="Rate Type">
                  {rateTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="base_plan_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Base Plan ID"
                type="number"
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="markup_type"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel>Markup Type</InputLabel>
                <Select {...field} label="Markup Type">
                  {markupTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="markup_value"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Markup Value"
                type="number"
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="inclusion_codes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Inclusion Codes (comma separated)"
                size="small"
                value={
                  Array.isArray(field.value)
                    ? field.value.join(",")
                    : field.value
                }
                onChange={(e) => field.onChange(e.target.value.split(","))}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="min_length_of_stay"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Min Length of Stay"
                type="number"
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="max_length_of_stay"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Max Length of Stay"
                type="number"
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="cancellation_policy_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Cancellation Policy ID"
                type="number"
                size="small"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select {...field} label="Status">
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {initialData ? "Update" : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default RatePlanForm;
